<template>
  <div class="text-white min-h-screen">
    <!-- Main Content Area -->
    <RouterView />

    <!-- Lazy-load heavy features component after browser is idle -->
    <!-- This dramatically reduces initial bundle size and JavaScript execution time -->
    <HeavyFeaturesLoader
      v-if="shouldLoadHeavyFeatures"
      ref="heavyFeaturesRef"
      :on-loaded="onHeavyFeaturesLoaded"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * App.vue - Root Application Component
 *
 * Initializes global application features:
 * - Account management (lightweight - immediate)
 * - Heavy features (DEFERRED after idle via async component):
 *   - Prediction history tracking (polls every 10s)
 *   - App-level reactions (confetti, success animations)
 *   - WebSocket blockchain event subscriptions
 *   - Creator markets monitoring (polls every 10s)
 *   - Unsettled market alerts
 *   - Cache warming (pre-fetches contract config)
 *
 * **Performance Optimization:**
 * Heavy composables are lazy-loaded via async component after browser idle
 * This reduces initial bundle size and ensures proper Vue lifecycle context
 * Only accounts (lightweight) is loaded immediately for UI state
 *
 * **Lifecycle:**
 * 1. Mount: Initialize lightweight account management + schedule heavy features
 * 2. Idle/Timeout: Load HeavyFeaturesLoader component (code-split)
 * 3. Component setup: Heavy composables initialize with proper lifecycle context
 * 4. Watch account changes: Auto-subscribe to blockchain events
 */
import { defineAsyncComponent, watch, onMounted, ref } from 'vue';
import { logDEBUG } from '@/common/debug';
import useAccounts from '@/composables/accounts/useAccounts';

// Initialize lightweight account management immediately (needed for UI state)
const accounts = useAccounts();

// Lazy-load heavy features component (code-split into separate bundle)
const HeavyFeaturesLoader = defineAsyncComponent(() => import('@/components/HeavyFeaturesLoader.vue'));

// Track heavy features loading state
const shouldLoadHeavyFeatures = ref(false);
const heavyFeaturesLoaded = ref(false);
const heavyFeaturesRef = ref<{ blockchainEvents?: { subscribe: () => void } } | null>(null);

/**
 * Schedule heavy features to load after browser is idle
 * This ensures initial page load is fast and PageSpeed metrics are good
 */
onMounted(() => {
  const scheduleHeavyFeatures = () => {
    shouldLoadHeavyFeatures.value = true;
  };

  // Use requestIdleCallback to defer loading until browser is idle
  if ('requestIdleCallback' in window) {
    requestIdleCallback(scheduleHeavyFeatures, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(scheduleHeavyFeatures, 1000);
  }
});

/**
 * Called when heavy features component finishes initialization
 */
function onHeavyFeaturesLoaded() {
  heavyFeaturesLoaded.value = true;
  logDEBUG('Heavy features loaded successfully');
}

/**
 * Auto-subscribe to blockchain events when account is connected
 * Only runs after heavy composables are loaded
 */
watch(
  () => accounts.selectedAccount.value,
  (newAccount, oldAccount) => {
    if (!heavyFeaturesLoaded.value || !heavyFeaturesRef.value) {
      // Heavy composables not loaded yet, will subscribe after load
      return;
    }

    const blockchainEvents = heavyFeaturesRef.value.blockchainEvents;
    if (!blockchainEvents) {
      return;
    }

    if (newAccount) {
      logDEBUG('Account changed, subscribing to WebSocket for', newAccount.address);
      blockchainEvents.subscribe();
    } else if (oldAccount) {
      // Account became null - log for debugging but keep WebSocket connected
      logDEBUG('WARNING: Account became null!', {
        previousAddress: oldAccount.address,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack,
      });
    }
  },
  { immediate: false }, // Don't run immediately, wait for heavy composables to load
);
</script>

<style scoped></style>
